import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { SidebarHeader } from "@/components/dashboard/SidebarHeader";
import { SidebarNavigation } from "@/components/dashboard/SidebarNavigation";
import { SidebarFooter } from "@/components/dashboard/SidebarFooter";

interface DashboardSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

export default function DashboardSidebar({ isOpen, onToggle, isMobile }: DashboardSidebarProps) {
  const [profile, setProfile] = useState<any>(null);
  const [lastLogin, setLastLogin] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('User fetch error:', userError);
          throw userError;
        }

        if (!user) {
          console.log('No authenticated user found');
          navigate('/signin');
          return;
        }

        // Update last_login in profiles table
        const now = new Date().toISOString();
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ last_login: now })
          .eq('id', user.id);

        if (updateError) {
          console.error('Error updating last_login:', updateError);
        }

        // Fetch updated profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          toast({
            title: "Error fetching profile",
            description: "Please try refreshing the page.",
            variant: "destructive",
          });
          return;
        }

        setProfile(profileData);
        if (profileData?.last_login) {
          setLastLogin(profileData.last_login);
        }
      } catch (error: any) {
        console.error('Error in profile fetch process:', error);
        toast({
          title: "Error",
          description: "Failed to load profile data. Please try refreshing the page.",
          variant: "destructive",
        });
      }
    };

    fetchProfile();
  }, [navigate, toast]);

  return (
    <div className={cn(
      "fixed inset-y-0 left-0 z-30 flex flex-col bg-background border-r transition-all duration-300",
      isOpen ? "w-64" : "w-16",
      isMobile && "w-full"
    )}>
      <SidebarHeader isOpen={isOpen} isMobile={isMobile} onToggle={onToggle} />
      <SidebarNavigation isOpen={isOpen} isMobile={isMobile} />
      <SidebarFooter 
        isOpen={isOpen}
        isMobile={isMobile}
        profile={profile}
        lastLogin={lastLogin}
        onLogout={async () => {
          try {
            await supabase.auth.signOut();
            navigate('/signin');
            toast({
              title: "Logged out successfully",
              description: "You have been logged out of your account.",
            });
          } catch (error) {
            toast({
              title: "Error logging out",
              description: "There was a problem logging out. Please try again.",
              variant: "destructive",
            });
          }
        }}
      />
    </div>
  );
}